import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { RootState } from '../../../../../redux/root';
import InspectionsControls from './Controls/InspectionsControls';
import { getServerTime } from '../../../../../api/inspections';
import { validateInspectionDate } from '../../../utils';
import {
  setInspectionApiResults,
  setPermitInspectionsList,
  setSelectedPermit,
} from '../../../../../redux/slices/inspectionsSlice';
import { setBottomDrawerOpened } from '../../../../../redux/slices/generalSlice';
import { setErrors, setNotes } from '../../../../../redux/slices/InspectionsReportSlice';
import {
  useGetAllPermitInspectionsQuery,
  useUpdateInspectionsMutation,
} from '../../../../../redux/rtkQuery/apiSlice';
import { ModalDefaultDraggable } from '../../../../../molecules';

const HOLD_MESSAGE =
  'Inspection cannot be scheduled. This permit has a hold on it — check conditions or contact the office for more information.';

const EditInspectionPopup: FC<any> = ({ updateNewStatus, disableFromHold }) => {
  const { selectedInspection, selectedPermit, permitInspectionsList } = useSelector(
    (s: RootState) => s.inspections,
  );
  const { contact, errors, contactPhone, notes } = useSelector(
    (s: RootState) => s.inspectionReports,
  );
  const schedules = useSelector((s: RootState) => s.general.schedules);
  const bottomDrawerOpened = useSelector(
    (s: RootState) => s.general.bottomDrawerOpened,
  );
  const [inspectionDate, _setInspectionDate] = useState<string>('');
  const [showClose, setShowClose] = useState<boolean>(false);
  const [updateInspections] = useUpdateInspectionsMutation();
  const [permit, setPermit] = useState<number>(-1);
  const { data: allPermitInspections, refetch, isUninitialized } =
    useGetAllPermitInspectionsQuery(permit, { skip: permit === -1 });

  const dispatch = useDispatch();

  const setInspectionDate = (data: string) => {
    _setInspectionDate(data);
    setShowClose(false);
    dispatch(setInspectionApiResults({ apiMessage: undefined, isApiError: false }));
  };

  useEffect(() => {
    if (allPermitInspections) {
      const _permit = {
        ...selectedPermit,
        inspections: allPermitInspections,
      };
      dispatch(setSelectedPermit(_permit));
    }
  }, [allPermitInspections]);

  useEffect(() => {
    setInspectionDate(selectedInspection.inspectionDate);
    dispatch(setNotes(selectedInspection.information));
  }, [selectedInspection]);

  const onClose = () => dispatch(setBottomDrawerOpened(false));

  const onUpdate = async () => {
    if (showClose) {
      setShowClose(false);
      dispatch(setBottomDrawerOpened(false));
      return;
    }
    const reductedInspection = { ...selectedInspection };
    delete reductedInspection.report;
    const _inspection = {
      id: reductedInspection.id,
      contact: contact.id,
      phone_number: contact.phoneNumber,
      information: notes,
      inspection_date: format(new Date(inspectionDate), 'MM/dd/yyyy'),
      status: 29, // ready for scheduling
    };

    const estTime: any = await getServerTime();
    const now = estTime?.data?.estTime ? new Date(estTime.data.estTime) : new Date();

    let validation = validateInspectionDate(new Date(inspectionDate), schedules, now);
    let _errors = { ...errors };
    if (validation !== null) {
      _errors = { ..._errors, inspectionDate: validation };
      dispatch(setErrors(_errors));
    }

    const phoneRegex = /^[0-9][0-9][0-9]-[0-9][0-9][0-9]-[0-9][0-9][0-9][0-9]$/;
    if (!phoneRegex.test(contactPhone)) {
      validation = 'Phone has to follow the pattern ###-###-####';
      _errors = { ..._errors, contactPhone: validation };
      dispatch(setErrors(_errors));
    }
    if (validation === null) {
      try {
        const result: any = await updateInspections(_inspection);
        dispatch(setInspectionApiResults({ apiMessage: 'Success', isApiError: false }));
        updateNewStatus(_inspection.id);
        setShowClose(true);

        let _selectedPermit;
        const updated = permitInspectionsList.map(p => {
          if (p.permitId === result.data.permit) {
            _selectedPermit = {
              ...p,
              inspections: p.inspections.map(pi => {
                if (pi.id === result.data.id) {
                  return { ...pi, ...result.data };
                }
                return pi;
              }),
            };
            return _selectedPermit;
          }
          return p;
        });
        dispatch(setPermitInspectionsList(updated));
        setPermit(_selectedPermit.permitId);
        if (!isUninitialized) {
          await refetch();
        }
      } catch (e) {
        setShowClose(true);
        if (e && e.response) {
          dispatch(
            setInspectionApiResults({
              apiMessage: JSON.stringify(e.response.data),
              isApiError: true,
            }),
          );
        }
      }
    }
  };

  if (!bottomDrawerOpened) return null;

  const submitLabel = showClose ? 'Close' : 'Schedule';
  const submitDisabled =
    !showClose && (Object.keys(contact).length === 0 || disableFromHold);

  return (
    <ModalDefaultDraggable
      title="Update Inspection"
      onClose={onClose}
      wide
      addContactModal
    >
      <FormBody>
        <InspectionsControls
          inspection={selectedInspection}
          setInspectionDate={setInspectionDate}
        />
      </FormBody>
      <StickyFooter>
        {disableFromHold && <HoldMessage>{HOLD_MESSAGE}</HoldMessage>}
        <FooterButtons>
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            disabled={submitDisabled}
            onClick={onUpdate}
          >
            {submitLabel}
          </PrimaryButton>
        </FooterButtons>
      </StickyFooter>
    </ModalDefaultDraggable>
  );
};

export default EditInspectionPopup;

const FormBody = styled.div`
  padding-bottom: 88px;
`;

const StickyFooter = styled.div`
  position: sticky;
  bottom: -35px;
  margin: 0 -35px -35px;
  padding: 14px 28px;
  background: var(--neutral-0);
  border-top: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 2;
`;

const FooterButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const HoldMessage = styled.div`
  font-size: 0.8125rem;
  color: var(--danger-700, #991b1b);
  background: var(--danger-50, #fee2e2);
  border: 1px solid var(--danger-200, #fecaca);
  border-radius: 6px;
  padding: 8px 12px;
  text-align: center;
`;

const PrimaryButton = styled.button`
  min-width: 140px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    background: var(--neutral-300);
    color: var(--neutral-0);
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  min-width: 100px;
  height: 40px;
  padding: 0 18px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;
